import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";
import { validateList } from "./validateResponse";
import { ProgramsSchema } from "../schema/zod";

const BASE_URL = config.BASE_URL;

// GET
// Authenticated/admin program list — returns ALL programs regardless
// of visibility or archived state (deferred filtering up to the caller).
// Used by 10+ staff/admin flows (enrollment, athlete edit, event create,
// registration wizard, etc.) that need to see the full set.
export const getPrograms = () => {
  return axios
    .get(`${BASE_URL}/programs`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET — programs + slots this caller may enroll into, filtered
// SERVER-side by the authenticated role (the available_for_registration
// self-service gate is bypassed for staff/admin/owner on the server, so
// no surface can forget the bypass again). Returns
// { programs, can_enroll_any_program } or null on failure. Programs with
// no enrollable slots are dropped. Prefer this over getPrograms +
// client filtering on every enrollment-picker surface.
export const getEnrollablePrograms = (token) => {
  return axios
    .get(`${BASE_URL}/programs/enrollable`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      const payload = res?.data?.data || null;
      // Two different failures, two different guards. schema/tables.ts
      // catches the CLIENT inventing a field — that is what `p.status` was,
      // and this check would have passed it happily, because the server was
      // right. This catches the other direction: the server dropping or
      // renaming a column, or sending null where the schema says it cannot.
      // That arrives as undefined, takes the falsy branch, and renders an
      // empty list without throwing. It reports; it does not reject.
      validateList(
        ProgramsSchema.pick({ id: true, key: true, label: true, archived_at: true }),
        payload?.programs,
        { operation: "GET /programs/enrollable" }
      );
      return payload;
    })
    .catch((err) => {
      captureException(err);
      return null;
    });
};

// GET — programs + slots that RUN on a given date (YYYY-MM-DD), the
// Mon-first day-index slot resolution done SERVER-side. Returns
// { date, day_index, programs } or null on failure. Used by the
// attendance page; the client no longer re-derives the weekday index.
export const getProgramsForDate = (token, date) => {
  return axios
    .get(`${BASE_URL}/programs/for-date`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params: { date },
    })
    .then((res) => res?.data?.data || null)
    .catch((err) => {
      captureException(err);
      return null;
    });
};

// GET — public programs list. Unauthenticated. Server-side filtered to
// visibility='public' AND archived_at IS NULL. Used by the public
// marketing /programs page; do NOT use for admin/staff flows.
export const getPublicPrograms = () => {
  return axios
    .get(`${BASE_URL}/public/programs`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getProgramsTimeSLots = (token) => {
  return axios
    .get(`${BASE_URL}/programs/programs-with-slots`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST
export const createProgramWithSlots = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/programs/add-with-slots`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response?.data;
    });
};

// PUT
// Rethrows on failure. Swallowing the error here returned `undefined`, which
// the caller read as "not successful" — so a failed save showed NO toast at
// all: no success, no error, nothing. Staff re-clicked Save and assumed it had
// worked (Stacy 2026-07-13, "it wouldnt stay"). The caller's catch owns the
// user-facing message now.
export const updateProgramWithSlots = (token, id, newForm) => {
  return axios
    .put(`${BASE_URL}/programs/${id}`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getProgramCategories = () => {
  return axios
    .get(`${BASE_URL}/programs/get-program-categories`, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      // REJECTS deliberately. Resolving `undefined` here made a failed load
      // indistinguishable from empty data: callers do `if (res?.success)` and
      // simply skip, leaving the program-category list silently absent. That is the
      // 2026-08-12 blank-picker bug. Callers MUST surface this.
      captureException(err);
      throw err;
    });
};

// POST — Natural-language program search (gap #7). Public; no auth header.
export const searchProgramsNL = (query) => {
  return axios
    .post(`${BASE_URL}/programs/search`, { query }, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => res?.data)
    // Rethrows so a failure reaches the caller's catch. Returning null made
    // CreateProgramPage toast "No similar programs to draw from yet" on a
    // network error — a claim about the tenant's DATA, not the request.
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET — Form auto-fill suggestions for new programs (gap #13).
// Returns mode/median typical values from existing programs + slots,
// optionally filtered by category_id.
export const getProgramSuggestions = (token, categoryId = null) => {
  const url = categoryId
    ? `${BASE_URL}/programs/suggestions?category_id=${encodeURIComponent(categoryId)}`
    : `${BASE_URL}/programs/suggestions`;
  return axios
    .get(url, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => { captureException(err); return null; });
};

// POST
export const addProgramCategory = (token, newForm) => {
  return axios
    .post(`${BASE_URL}/programs/add-program-categories`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT - Update program category
export const updateProgramCategory = (token, categoryId, data) => {
  return axios
    .put(`${BASE_URL}/programs/categories/${categoryId}`, data, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET - List programs including archived (for restore UI)
export const getProgramsIncludingArchived = (token) => {
  return axios
    .get(`${BASE_URL}/programs?include_archived=true`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET - Impact preview for archive/delete
export const getProgramDeleteImpact = (token, programId) => {
  return axios
    .get(`${BASE_URL}/programs/${programId}/delete-impact`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// POST - Archive a program (with optional cascade flags)
export const archiveProgram = (token, programId, opts = {}) => {
  return axios
    .post(
      `${BASE_URL}/programs/${programId}/archive`,
      {
        archive_enrollments: opts.archive_enrollments !== false,
        archive_attendance: opts.archive_attendance !== false,
      },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// POST - Restore an archived program
export const restoreProgram = (token, programId) => {
  return axios
    .post(`${BASE_URL}/programs/${programId}/restore`, null, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};

// DELETE - Hard delete a program (only succeeds when impact is clean)
export const hardDeleteProgram = (token, programId) => {
  return axios
    .delete(`${BASE_URL}/programs/${programId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => res?.data)
    .catch((err) => err?.response?.data || { success: false });
};
